import { useState, useEffect, useCallback } from "react";
import { Capacitor } from "@capacitor/core";

interface BiometricState {
  isAvailable: boolean;
  biometryType: "face" | "fingerprint" | "none";
  isLoading: boolean;
  isEnabled: boolean;
  error: string | null;
}

const BIOMETRIC_ENABLED_KEY = "biometric_auth_enabled";

/**
 * Hook for managing biometric authentication (Face ID / Touch ID)
 */
export function useBiometricAuth() {
  const [state, setState] = useState<BiometricState>({
    isAvailable: false,
    biometryType: "none",
    isLoading: true,
    isEnabled: false,
    error: null,
  });

  // Check if biometric is available
  const checkAvailability = useCallback(async () => {
    if (!Capacitor.isNativePlatform()) {
      setState(prev => ({ ...prev, isLoading: false, isAvailable: false }));
      return;
    }

    try {
      const { NativeBiometric, BiometryType } = await import("capacitor-native-biometric");
      const result = await NativeBiometric.isAvailable();
      
      let biometryType: "face" | "fingerprint" | "none" = "none";
      if (result.isAvailable) {
        if (result.biometryType === BiometryType.FACE_ID || 
            result.biometryType === BiometryType.FACE_AUTHENTICATION) {
          biometryType = "face";
        } else if (result.biometryType === BiometryType.TOUCH_ID || 
                   result.biometryType === BiometryType.FINGERPRINT) {
          biometryType = "fingerprint";
        }
      }

      // Check if user has enabled biometric auth
      const isEnabled = localStorage.getItem(BIOMETRIC_ENABLED_KEY) === "true";

      setState({
        isAvailable: result.isAvailable,
        biometryType,
        isLoading: false,
        isEnabled,
        error: null,
      });
    } catch (error) {
      console.error("Biometric check error:", error);
      setState(prev => ({
        ...prev,
        isLoading: false,
        isAvailable: false,
        error: "Impossible de vérifier la biométrie",
      }));
    }
  }, []);

  useEffect(() => {
    checkAvailability();
  }, [checkAvailability]);

  /**
   * Authenticate with biometrics
   * Returns true if successful, false otherwise
   */
  const authenticate = useCallback(async (reason?: string): Promise<boolean> => {
    if (!state.isAvailable) return false;

    try {
      const { NativeBiometric } = await import("capacitor-native-biometric");
      
      await NativeBiometric.verifyIdentity({
        reason: reason || "Authentifiez-vous pour accéder à vos documents",
        title: "EasyFinder",
        subtitle: state.biometryType === "face" ? "Face ID" : "Touch ID",
        description: "Vérification de votre identité",
        maxAttempts: 3,
      });
      
      return true;
    } catch (error: any) {
      console.error("Biometric auth error:", error);
      // User cancelled or failed
      return false;
    }
  }, [state.isAvailable, state.biometryType]);

  /**
   * Enable biometric authentication for the app
   */
  const enableBiometric = useCallback(async (): Promise<boolean> => {
    // First verify the user can authenticate
    const success = await authenticate("Activez la biométrie pour sécuriser l'app");
    
    if (success) {
      localStorage.setItem(BIOMETRIC_ENABLED_KEY, "true");
      setState(prev => ({ ...prev, isEnabled: true }));
    }
    
    return success;
  }, [authenticate]);

  /**
   * Disable biometric authentication
   */
  const disableBiometric = useCallback(() => {
    localStorage.removeItem(BIOMETRIC_ENABLED_KEY);
    setState(prev => ({ ...prev, isEnabled: false }));
  }, []);

  /**
   * Get display name for the biometry type
   */
  const getBiometryName = useCallback(() => {
    if (state.biometryType === "face") return "Face ID";
    if (state.biometryType === "fingerprint") return "Touch ID";
    return "Biométrie";
  }, [state.biometryType]);

  return {
    ...state,
    authenticate,
    enableBiometric,
    disableBiometric,
    getBiometryName,
    checkAvailability,
  };
}
